import {
  useLayoutEffect,
  useMemo,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { computePlacement } from "@/lib/edge-aware-placement";
import { measureUntransformed } from "@/lib/measure-untransformed";
import type { CursorAnchor } from "./input-bar-types";

const INSET = 12;

interface CursorBarPlacementOptions {
  cursorAnchor: CursorAnchor | null;
  /** Width to apply when in compact mode. */
  compactWidth: number;
  compact: boolean;
  placement?: "cursor" | "bottom";
}

interface PlacementBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface PlacementViewport {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
}

interface CursorBarPositionInput {
  base: { x: number; y: number };
  box: PlacementBox;
  compact: boolean;
  inset?: number;
  placement: "cursor" | "bottom";
  viewport: PlacementViewport;
}

interface CursorBarRectInput {
  anchor: { x: number; y: number };
  compact: boolean;
  inset?: number;
  placement: "cursor" | "bottom";
  size: { width: number; height: number };
  viewport: PlacementViewport;
}

export interface CursorBarRect {
  x: number;
  y: number;
  width: number;
  height: number;
  maxWidth: number;
}

function anchorBase(cursorAnchor: CursorAnchor | null): {
  x: number;
  y: number;
} {
  if (!cursorAnchor) return { x: 0, y: 0 };
  if (cursorAnchor.type === "flow") {
    return {
      x: cursorAnchor.flowX * cursorAnchor.zoom + cursorAnchor.viewportX,
      y: cursorAnchor.flowY * cursorAnchor.zoom + cursorAnchor.viewportY,
    };
  }
  return { x: cursorAnchor.localX, y: cursorAnchor.localY };
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

export function computeCursorBarRect({
  anchor,
  compact,
  inset = INSET,
  placement,
  size,
  viewport,
}: CursorBarRectInput): CursorBarRect {
  const maxWidth = Math.max(0, viewport.width - inset * 2);
  const width = maxWidth > 0 ? Math.min(size.width, maxWidth) : size.width;
  const height = size.height;
  const minLeft = viewport.left + inset;
  const maxLeft = viewport.right - inset - width;
  const minTop = viewport.top + inset;
  const maxTop = viewport.bottom - inset - height;

  let x: number;
  let y: number;
  if (placement === "bottom") {
    x = viewport.left + (viewport.width - width) / 2;
    y = viewport.bottom - inset - height;
  } else if (compact) {
    x = anchor.x - width / 2;
    y = anchor.y;
    if (y + height > viewport.bottom - inset) y = anchor.y - height;
  } else {
    const result = computePlacement({
      anchor,
      size: { width, height },
      viewport: {
        left: viewport.left,
        top: viewport.top,
        right: viewport.right,
        bottom: viewport.bottom,
      },
      inset,
      anchorOffset: 0,
    });
    x = result.left;
    y = result.top;
  }

  return {
    x: clamp(x, minLeft, maxLeft),
    y: clamp(y, minTop, maxTop),
    width,
    height,
    maxWidth,
  };
}

export function computeCursorBarPosition({
  base,
  box,
  compact,
  inset = INSET,
  placement,
  viewport,
}: CursorBarPositionInput): {
  maxWidth: number;
  translateX: number;
  translateY: number;
} {
  const anchor = {
    x: box.left + base.x,
    y: box.top + base.y,
  };
  const rect = computeCursorBarRect({
    anchor,
    compact,
    inset,
    placement,
    size: { width: box.width, height: box.height },
    viewport,
  });

  return {
    maxWidth: rect.maxWidth,
    translateX: rect.x - box.left - (placement === "bottom" ? 0 : base.x),
    translateY: rect.y - box.top - (placement === "bottom" ? 0 : base.y),
  };
}

/**
 * Computes the absolute-positioned wrapper style for the cursor-anchored
 * input bar, including an edge-aware nudge.
 *
 * After each render/resize, measures the untransformed bounding box and
 * shifts it inward via a translate so it never clips the viewport.
 * Returns the inline `style` to apply to the wrapper, or `undefined` when
 * not in cursor mode.
 */
export function useCursorBarPlacement(
  containerRef: RefObject<HTMLElement | null>,
  {
    cursorAnchor,
    compactWidth,
    compact,
    placement = "cursor",
  }: CursorBarPlacementOptions,
): CSSProperties | undefined {
  const [positionState, setPositionState] = useState<{
    maxWidth?: number;
    translateX: number;
    translateY: number;
  }>({ translateX: 0, translateY: 0 });

  const deps = useMemo(
    () => [
      ...(cursorAnchor
        ? cursorAnchor.type === "flow"
          ? [
              cursorAnchor.flowX,
              cursorAnchor.flowY,
              cursorAnchor.viewportX,
              cursorAnchor.viewportY,
              cursorAnchor.zoom,
            ]
          : [cursorAnchor.localX, cursorAnchor.localY]
        : []),
      compact,
      placement,
    ],
    [cursorAnchor, compact, placement],
  );

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const box = measureUntransformed(el);
      const pane =
        (el.closest("[data-canvas-viewport]") as HTMLElement | null) ??
        document.documentElement;
      const v = pane.getBoundingClientRect();
      const base = anchorBase(cursorAnchor);

      const next = computeCursorBarPosition({
        base,
        box,
        compact,
        placement,
        viewport: {
          left: v.left,
          top: v.top,
          right: v.right,
          bottom: v.bottom,
          width: v.width,
        },
      });
      setPositionState((prev) =>
        prev.translateX === next.translateX &&
        prev.translateY === next.translateY &&
        prev.maxWidth === next.maxWidth
          ? prev
          : next,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const pane =
      (el.closest("[data-canvas-viewport]") as HTMLElement | null) ??
      document.documentElement;
    ro.observe(pane);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  if (!cursorAnchor) return undefined;

  const { x: baseX, y: baseY } = anchorBase(cursorAnchor);
  const transform =
    placement === "bottom"
      ? `translate(${positionState.translateX}px, ${positionState.translateY}px)`
      : `translate(${baseX + positionState.translateX}px, ${baseY + positionState.translateY}px)`;

  return {
    position: "absolute",
    left: 0,
    top: 0,
    width: compact
      ? positionState.maxWidth === undefined
        ? `${compactWidth}px`
        : `min(${compactWidth}px, ${positionState.maxWidth}px)`
      : undefined,
    // Width only — matches the agent stack's internal 200ms ease-out width
    // animation so bar and stack grow in lockstep and the textarea keeps
    // its width (no wrap, no height jump). Transform must NOT animate:
    // it repositions instantly on anchor moves / edge clamping.
    transition: compact ? "width 200ms ease-out" : undefined,
    transform,
    transformOrigin: "top left",
  };
}
